// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { Table } from '@shared/ui-kit';
import { formatToMoney } from '@advisor-app/features/utils/format-to-money';
import {
  type RebalanceReportingTargetResp,
  type RebalanceReportingTaxesResp,
} from '@shared/data-access/interfaces/api/ai/portfolio-rebalance';
import { type PositionsAnalysis } from '@shared/data-access/interfaces/redux/portfolio-state';
import type { KpiBoxesProps, PopupsName } from '@shared/ui-kit/components';
import {
  KpiBoxes,
  PageContainer,
  SectionContainer,
} from '@shared/ui-kit/components';
import { useMemo } from 'react';
import { renderColumnsTradesRebalance } from './render-columns-trades-rebalance';
import { useTranslation } from 'react-i18next';

interface TradesProps {
  tax: RebalanceReportingTaxesResp;
  deviation: RebalanceReportingTargetResp;
  handleDeleteOption: (ticker: string, index?: number) => Promise<void>;
  rebalanceTableData: PositionsAnalysis[];
}

export const Tax = ({
  tax,
  deviation,
  handleDeleteOption,
  rebalanceTableData,
}: TradesProps) => {
  const { t } = useTranslation();
  const hasOnlyOneData = useMemo(
    () => rebalanceTableData.length === 1,
    [rebalanceTableData],
  );

  const kpiData: KpiBoxesProps[] = [
    {
      value: deviation?.mean_absolute_error,
      text: t('rebalance.deviation-target-portfolio', { ns: 'rebalance' }),
      popupName: 'kpis.deviation',
      icon: 'Target',
      color: 'main',
    },
    {
      value: tax.capital_gains.short_term + tax.capital_gains.long_term,
      text: t('glossary.kpi-realized-gains', { ns: 'glossary' }),
      popupName: 'kpis.realizedGains',
      icon: 'DollarSign',
      color: 'success',
    },
    {
      value: tax.capital_gains.short_term,
      text: t('rebalance.realized-st', { ns: 'rebalance' }),
      numberPositions: 0,
      popupName: 'kpis.realizedSt',
      icon: 'Clock',
      color: 'secondary',
    },
    {
      value: tax.capital_gains.long_term,
      text: t('rebalance.realized-lt', { ns: 'rebalance' }),
      popupName: 'kpis.realizedLt',
      icon: 'Calendar',
      color: 'main',
    },
  ];

  return (
    <PageContainer className="!m-0 !p-0">
      <div className="mb-4 mt-2 grid grid-cols-4 gap-6">
        {kpiData.map(
          ({ numberPositions, text, value, popupName, icon, color }, idx) => (
            <KpiBoxes
              key={text}
              value={
                idx === 0
                  ? `${(+value * 100).toFixed(1)}%`
                  : formatToMoney(Math.trunc(+value))
              }
              text={text}
              numberPositions={numberPositions || undefined}
              popupName={popupName as PopupsName}
              icon={icon}
              color={color}
            />
          ),
        )}
      </div>
      <SectionContainer className="!p-5">
        <div className="overflow-x-auto rounded">
          <Table.Grouped
            columns={renderColumnsTradesRebalance({
              tickerMethod: handleDeleteOption,
              hasOnlyOneData,
            })}
            data={rebalanceTableData}
            emptyState={false}
          />
        </div>
      </SectionContainer>
    </PageContainer>
  );
};
